import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SPORTS, DEFAULT_SPORT } from "@/lib/sports";
import { invalidateCircuitConfig } from "@/lib/circuits";
import {
    Trophy, Check, Trash2, Plus, ChevronDown, ChevronUp, Pencil, GripVertical,
    Flag, Loader2, X, MoveRight,
} from "lucide-react";

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `t_${Math.random().toString(36).slice(2)}`);

// ---- inline rename input --------------------------------------------------
function InlineRename({ value, onSave, onCancel, testid }) {
    const [v, setV] = useState(value);
    return (
        <span className="inline-flex items-center gap-1">
            <input
                autoFocus
                data-testid={`${testid}-input`}
                value={v}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSave(v); if (e.key === "Escape") onCancel(); }}
                className="h-7 bg-[#141414] border border-[#3a3a3a] px-2 text-sm text-white outline-none min-w-[10rem]"
            />
            <button data-testid={`${testid}-save`} onClick={() => onSave(v)} className="h-7 w-7 flex items-center justify-center bg-[#22c55e] text-black"><Check className="h-3.5 w-3.5" /></button>
            <button data-testid={`${testid}-cancel`} onClick={onCancel} className="h-7 w-7 flex items-center justify-center border border-[#3a3a3a] text-[#a0a0a0]"><X className="h-3.5 w-3.5" /></button>
        </span>
    );
}

// ---- one sport section (Example 1: split editor + live preview) -----------
function SportEditor({ sport, data, onChange, onReloadSport, reloadCounts }) {
    const [renameCircuit, setRenameCircuit] = useState(null); // circuit name being renamed
    const [renameTier, setRenameTier] = useState(null);       // tier id being renamed
    const [saveState, setSaveState] = useState("idle");       // idle | saving | saved
    const [addName, setAddName] = useState("");
    const [adding, setAdding] = useState(false);
    const dragRef = useRef(null); // { name, from } where from = {type:'tier',id} | {type:'unsorted'}
    const saveTimer = useRef(null);

    const { tiers = [], unsorted = [], pending = [] } = data || {};

    const persist = async (tiersToSave) => {
        try {
            setSaveState("saving");
            await api.put("/circuits/admin/config", {
                sport,
                tiers: tiersToSave.map((t) => ({ id: t.id, name: t.name, circuits: t.circuits.map((c) => c.name) })),
            });
            invalidateCircuitConfig(sport);
            setSaveState("saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save order");
            setSaveState("idle");
        }
    };

    // Every structural change flows through setData → autosaves (debounced).
    const setData = (next) => {
        onChange(next);
        setSaveState("saving");
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => persist(next.tiers), 500);
    };

    useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

    // --- circuit movement helpers ---
    const locList = (d, loc) => (loc.type === "unsorted" ? d.unsorted : d.tiers.find((t) => t.id === loc.id)?.circuits);

    const moveCircuit = (name, to) => {
        const d = { tiers: tiers.map((t) => ({ ...t, circuits: [...t.circuits] })), unsorted: [...unsorted], pending };
        // remove from wherever it is
        let item = null;
        d.unsorted = d.unsorted.filter((c) => (c.name === name ? ((item = c), false) : true));
        d.tiers.forEach((t) => { t.circuits = t.circuits.filter((c) => (c.name === name ? ((item = c), false) : true)); });
        if (!item) return;
        const target = locList(d, to);
        if (target) target.push(item);
        setData(d);
    };

    const reorder = (loc, idx, dir) => {
        const d = { tiers: tiers.map((t) => ({ ...t, circuits: [...t.circuits] })), unsorted: [...unsorted], pending };
        const list = locList(d, loc);
        const j = idx + dir;
        if (j < 0 || j >= list.length) return;
        [list[idx], list[j]] = [list[j], list[idx]];
        setData(d);
    };

    const addTier = () => setData({ tiers: [...tiers, { id: uid(), name: `Tier ${tiers.length + 1}`, circuits: [] }], unsorted, pending });
    const doRenameTier = (id, name) => {
        const nm = (name || "").trim();
        if (!nm) return;
        setData({ tiers: tiers.map((t) => (t.id === id ? { ...t, name: nm } : t)), unsorted, pending });
        setRenameTier(null);
    };
    const deleteTier = (id) => {
        const t = tiers.find((x) => x.id === id);
        if (!t) return;
        setData({ tiers: tiers.filter((x) => x.id !== id), unsorted: [...unsorted, ...t.circuits], pending });
    };
    const moveTier = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= tiers.length) return;
        const nt = [...tiers];
        [nt[idx], nt[j]] = [nt[j], nt[idx]];
        setData({ tiers: nt, unsorted, pending });
    };

    // --- persisted actions (cascade / server) ---
    const submitRename = async (oldName, newName) => {
        const nm = (newName || "").trim();
        setRenameCircuit(null);
        if (!nm || nm === oldName) return;
        try {
            await api.post("/circuits/rename", { sport, old_name: oldName, new_name: nm });
            invalidateCircuitConfig(sport);
            toast.success(`Renamed to "${nm}" — all linked teams & events updated`);
            onReloadSport();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
    };

    const verify = async (c) => {
        try { await api.post(`/circuits/${c.id}/verify`); invalidateCircuitConfig(sport); toast.success(`"${c.name}" verified`); onReloadSport(); reloadCounts(); }
        catch (e) { toast.error("Could not verify"); }
    };
    const removeCircuit = async (c) => {
        const warn = (c.usage?.teams || c.usage?.events) ? ` It's used by ${c.usage.teams} team(s) and ${c.usage.events} event(s), which will revert to "Other".` : "";
        if (!window.confirm(`Delete circuit "${c.name}"?${warn}`)) return;
        try { await api.delete(`/circuits/${c.id}`); invalidateCircuitConfig(sport); toast.success("Circuit deleted"); onReloadSport(); reloadCounts(); }
        catch (e) { toast.error("Could not delete"); }
    };

    const addCircuit = async () => {
        const nm = addName.trim();
        if (!nm || adding) return;
        setAdding(true);
        try {
            const r = await api.post("/circuits/admin/add", { sport, name: nm });
            invalidateCircuitConfig(sport);
            toast.success(r.data.existed ? `"${r.data.name}" already existed — it's now verified` : `Added "${r.data.name}"`);
            setAddName("");
            onReloadSport();
            reloadCounts();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not add circuit"); }
        finally { setAdding(false); }
    };

    // --- circuit row ---
    const moveOptions = [...tiers.map((t) => ({ label: t.name, to: { type: "tier", id: t.id } })), { label: "Unsorted", to: { type: "unsorted" } }];

    const CircuitRow = ({ c, loc, idx, list }) => (
        <div
            draggable
            onDragStart={() => { dragRef.current = { name: c.name, from: loc }; }}
            onDragEnd={() => { dragRef.current = null; }}
            className="flex items-center gap-2 px-3 py-2 border border-[#2a2a2a] bg-[#141414] mb-1.5"
            data-testid={`cm-circuit-${sport}-${c.name}`}
        >
            <GripVertical className="h-4 w-4 text-[#555] cursor-grab shrink-0" />
            <div className="flex-1 min-w-0">
                {renameCircuit === c.name ? (
                    <InlineRename value={c.name} onSave={(v) => submitRename(c.name, v)} onCancel={() => setRenameCircuit(null)} testid={`cm-rename-circuit-${c.name}`} />
                ) : (
                    <span className="text-sm text-white truncate flex items-center gap-2">
                        {c.name}
                        {c.official && <span className="text-[9px] uppercase tracking-wider text-[#666]">official</span>}
                        <button data-testid={`cm-rename-btn-${sport}-${c.name}`} onClick={() => setRenameCircuit(c.name)} className="text-[#888] hover:text-white" title="Rename circuit"><Pencil className="h-3 w-3" /></button>
                    </span>
                )}
                <div className="text-[11px] text-[#666]">{c.usage?.teams || 0} teams · {c.usage?.events || 0} events</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => reorder(loc, idx, -1)} disabled={idx === 0} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] disabled:opacity-30" data-testid={`cm-up-${sport}-${c.name}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => reorder(loc, idx, 1)} disabled={idx === list.length - 1} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] disabled:opacity-30" data-testid={`cm-down-${sport}-${c.name}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                <select
                    value=""
                    onChange={(e) => { const t = moveOptions[Number(e.target.value)]; if (t) moveCircuit(c.name, t.to); }}
                    className="h-6 bg-[#0f0f0f] border border-[#2a2a2a] text-[11px] text-[#a0a0a0] px-1 max-w-[7rem]"
                    data-testid={`cm-moveto-${sport}-${c.name}`}
                    title="Move to tier"
                >
                    <option value="">Move to…</option>
                    {moveOptions.map((o, i) => <option key={o.label + i} value={i}>{o.label}</option>)}
                </select>
                {c.id && (
                    <button onClick={() => removeCircuit(c)} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:text-red-500 hover:border-red-500" data-testid={`cm-del-${sport}-${c.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
            </div>
        </div>
    );

    const dropInto = (loc) => (e) => {
        e.preventDefault();
        const d = dragRef.current;
        if (d && d.name) moveCircuit(d.name, loc);
        dragRef.current = null;
    };

    return (
        <div className="mb-8" data-testid={`cm-sport-${sport}`}>
            <div className="flex items-center gap-3 mb-3">
                <Trophy className="h-4 w-4 text-[#ff4500]" />
                <span className="font-display text-lg text-white uppercase tracking-wide">{sport}</span>
                {pending.length > 0 && (
                    <span className="text-[11px] uppercase tracking-wider text-[#f59e0b]" data-testid={`cm-sport-pending-${sport}`}>{pending.length} pending</span>
                )}
                <span className="flex-1 h-px bg-[#242424]" />
                <span data-testid={`cm-save-status-${sport}`} className="h-8 px-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#a0a0a0]">
                    {saveState === "saving"
                        ? <><Loader2 className="h-3.5 w-3.5 animate-spin text-[#ff4500]" /> Saving…</>
                        : <><Check className="h-3.5 w-3.5 text-[#22c55e]" /> Auto-saved</>}
                </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
                {/* LEFT: editor */}
                <div>
                    {/* Add circuit */}
                    <div className="flex items-center gap-2 mb-4" data-testid={`cm-add-circuit-${sport}`}>
                        <input
                            value={addName}
                            onChange={(e) => setAddName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") addCircuit(); }}
                            placeholder={`Add a ${sport} circuit…`}
                            data-testid={`cm-add-circuit-input-${sport}`}
                            className="flex-1 h-9 bg-[#141414] border border-[#2a2a2a] px-3 text-sm text-white placeholder:text-[#555] outline-none focus:border-[#ff4500]"
                        />
                        <button
                            onClick={addCircuit}
                            disabled={!addName.trim() || adding}
                            data-testid={`cm-add-circuit-btn-${sport}`}
                            className="h-9 px-3 flex items-center gap-1.5 bg-[#ff4500] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40"
                        >
                            <Plus className="h-4 w-4" /> Add
                        </button>
                    </div>

                    {/* Awaiting verification */}
                    {pending.length > 0 && (
                        <div className="mb-4" data-testid={`cm-pending-${sport}`}>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-[#f59e0b] mb-2 flex items-center gap-2"><Flag className="h-3.5 w-3.5" /> Awaiting verification ({pending.length})</div>
                            {pending.map((c) => (
                                <div key={c.id} className="flex items-center gap-2 px-3 py-2 border border-[#2a2a2a] bg-[#141414] mb-1.5" style={{ borderLeft: "3px solid #f59e0b" }} data-testid={`cm-pending-row-${c.name}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">{c.name} {c.created_by_username && <span className="text-[#666] text-xs">· {c.created_by_username}</span>}</div>
                                        <div className="text-[11px] text-[#666]">{c.usage?.teams || 0} teams · {c.usage?.events || 0} events</div>
                                    </div>
                                    <button onClick={() => verify(c)} className="h-7 px-2.5 flex items-center gap-1 bg-[#22c55e] text-black text-[11px] font-bold uppercase tracking-wider" data-testid={`cm-verify-${c.name}`}><Check className="h-3.5 w-3.5" /> Verify</button>
                                    <button onClick={() => removeCircuit(c)} className="h-7 w-7 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:text-red-500 hover:border-red-500" data-testid={`cm-pending-del-${c.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Tiers */}
                    {tiers.map((t, ti) => (
                        <div key={t.id} onDragOver={(e) => e.preventDefault()} onDrop={dropInto({ type: "tier", id: t.id })} data-testid={`cm-tier-${sport}-${ti}`}>
                            <div className="flex items-center gap-2 mt-4 mb-2">
                                {renameTier === t.id ? (
                                    <InlineRename value={t.name} onSave={(v) => doRenameTier(t.id, v)} onCancel={() => setRenameTier(null)} testid={`cm-rename-tier-${ti}`} />
                                ) : (
                                    <>
                                        <span className="text-[13px] uppercase tracking-[0.2em] text-[#ff4500] font-bold" data-testid={`cm-tier-name-${sport}-${ti}`}>{t.name}</span>
                                        <button onClick={() => setRenameTier(t.id)} className="text-[#888] hover:text-white" title="Rename tier" data-testid={`cm-tier-rename-${sport}-${ti}`}><Pencil className="h-3 w-3" /></button>
                                    </>
                                )}
                                <span className="flex-1 h-px bg-[#242424]" />
                                <button onClick={() => moveTier(ti, -1)} disabled={ti === 0} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                                <button onClick={() => moveTier(ti, 1)} disabled={ti === tiers.length - 1} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                                <button onClick={() => deleteTier(t.id)} className="h-6 w-6 flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:text-red-500 hover:border-red-500" title="Delete tier" data-testid={`cm-tier-del-${sport}-${ti}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                            {t.circuits.length === 0 && <div className="text-xs text-[#555] px-3 py-2 border border-dashed border-[#2a2a2a] mb-1.5">Drag circuits here or use “Move to…”</div>}
                            {t.circuits.map((c, ci) => <CircuitRow key={c.name} c={c} loc={{ type: "tier", id: t.id }} idx={ci} list={t.circuits} />)}
                        </div>
                    ))}

                    <button onClick={addTier} className="w-full mt-3 py-3 border border-dashed border-[#3a3a3a] text-[#a0a0a0] text-[12px] uppercase tracking-[0.14em] hover:border-[#ff4500] hover:text-white flex items-center justify-center gap-2" data-testid={`cm-add-tier-${sport}`}>
                        <Plus className="h-4 w-4" /> Add tier heading
                    </button>

                    {/* Unsorted */}
                    <div className="mt-6" onDragOver={(e) => e.preventDefault()} onDrop={dropInto({ type: "unsorted" })} data-testid={`cm-unsorted-${sport}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-[13px] uppercase tracking-[0.2em] text-[#a0a0a0] font-bold">Unsorted</span>
                            <span className="flex-1 h-px bg-[#242424]" />
                        </div>
                        {unsorted.length === 0 && <div className="text-xs text-[#555] px-3 py-2 border border-dashed border-[#2a2a2a]">Everything is placed in a tier.</div>}
                        {unsorted.map((c, ci) => <CircuitRow key={c.name} c={c} loc={{ type: "unsorted" }} idx={ci} list={unsorted} />)}
                    </div>
                </div>

                {/* RIGHT: live preview */}
                <div className="border border-[#2a2a2a] bg-[#0f0f0f] p-4 lg:sticky lg:top-4" data-testid={`cm-preview-${sport}`}>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-3">Main-page preview · {sport}</div>
                    <div className="border border-[#3a3a3a] bg-[#181818] px-3 py-2.5 flex items-center justify-between text-sm text-white">Circuits <ChevronDown className="h-4 w-4 text-[#a0a0a0]" /></div>
                    <div className="border border-[#2a2a2a] border-t-0">
                        {tiers.filter((t) => t.circuits.length).map((t) => (
                            <div key={t.id}>
                                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-[0.2em] text-[#f59e0b]">{t.name}</div>
                                {t.circuits.map((c) => <div key={c.name} className="px-3.5 py-2 text-sm text-white border-t border-[#1c1c1c]">{c.name}</div>)}
                            </div>
                        ))}
                        {unsorted.length > 0 && (
                            <div>
                                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-[0.2em] text-[#666]">{tiers.some((t) => t.circuits.length) ? "Other circuits" : "Circuits"}</div>
                                {unsorted.map((c) => <div key={c.name} className="px-3.5 py-2 text-sm text-white border-t border-[#1c1c1c]">{c.name}</div>)}
                            </div>
                        )}
                    </div>
                    <div className="mt-3 text-[11px] text-[#666] border-l-2 border-[#ff4500] pl-2.5">Order & tier names here are exactly what fans see. <MoveRight className="inline h-3 w-3" /> Changes publish automatically.</div>
                </div>
            </div>
        </div>
    );
}

// ---- top-level manager ----------------------------------------------------
export default function CircuitsManager() {
    const [selected, setSelected] = useState([DEFAULT_SPORT]);
    const [counts, setCounts] = useState({ by_sport: {}, total: 0 });
    const [board, setBoard] = useState({});
    const [sportOpen, setSportOpen] = useState(false);
    const popRef = useRef(null);

    const reloadCounts = useCallback(() => {
        api.get("/circuits/admin/pending-counts").then((r) => setCounts(r.data || { by_sport: {}, total: 0 })).catch(() => {});
    }, []);

    const loadBoard = useCallback((sports) => {
        if (!sports.length) return;
        api.get(`/circuits/admin/board?sports=${sports.map(encodeURIComponent).join(",")}`)
            .then((r) => setBoard((b) => ({ ...b, ...(r.data.sports || {}) })))
            .catch(() => {});
    }, []);

    useEffect(() => { reloadCounts(); }, [reloadCounts]);
    useEffect(() => {
        const missing = selected.filter((s) => !board[s]);
        if (missing.length) loadBoard(missing);
    }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!sportOpen) return undefined;
        const h = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setSportOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [sportOpen]);

    const toggleSport = (s) => setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

    const orderedSelected = useMemo(() => SPORTS.filter((s) => selected.includes(s)), [selected]);

    const reloadSport = (sport) => () => loadBoard([sport]);

    return (
        <div data-testid="admin-circuits-manager">
            <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-[#f59e0b] font-bold mb-4" data-testid="cm-total-pending">
                <Flag className="h-3.5 w-3.5" />
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-[#f59e0b] text-black rounded-full text-xs font-extrabold">{counts.total}</span>
                verification{counts.total === 1 ? "" : "s"} awaiting across all sports
            </div>

            {/* Sport multiselect */}
            <div className="relative inline-block mb-6" ref={popRef}>
                <button onClick={() => setSportOpen((o) => !o)} data-testid="cm-sport-select" className="h-10 min-w-[240px] flex items-center justify-between gap-3 px-3 border border-[#2a2a2a] bg-[#141414] text-sm text-white">
                    <span><span className="text-[11px] uppercase tracking-wider text-[#666] mr-2">Sports</span>{orderedSelected.length ? orderedSelected.join(", ") : "Select sports"}</span>
                    <ChevronDown className="h-4 w-4 text-[#a0a0a0]" />
                </button>
                {sportOpen && (
                    <div className="absolute z-40 mt-1 w-[280px] border border-[#2a2a2a] bg-[#0f0f0f] shadow-2xl max-h-80 overflow-y-auto" data-testid="cm-sport-menu">
                        {SPORTS.map((s) => {
                            const on = selected.includes(s);
                            const n = counts.by_sport?.[s] || 0;
                            return (
                                <div key={s} onClick={() => toggleSport(s)} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-[#181818]" data-testid={`cm-sport-opt-${s}`}>
                                    <span className={`h-4 w-4 border rounded-sm flex items-center justify-center flex-none ${on ? "border-transparent bg-[#ff4500]" : "border-[#444]"}`}>{on && <Check className="h-3 w-3 text-white" />}</span>
                                    <span className="flex-1 text-white">{s}</span>
                                    <span className={`text-xs font-bold ${n ? "text-[#f59e0b]" : "text-[#555]"}`}>{n}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {orderedSelected.length === 0 && <div className="text-sm text-[#666]">Pick one or more sports to manage their circuits.</div>}

            {orderedSelected.map((sport) => (
                board[sport]
                    ? <SportEditor key={sport} sport={sport} data={board[sport]} onChange={(d) => setBoard((b) => ({ ...b, [sport]: d }))} onReloadSport={reloadSport(sport)} reloadCounts={reloadCounts} />
                    : <div key={sport} className="text-sm text-[#666] mb-6" data-testid={`cm-loading-${sport}`}>Loading {sport}…</div>
            ))}
        </div>
    );
}
